import { TableHeader } from '@/ui/typography/company-info';
import { styled } from 'goober';

export const remapEmphasizedTextToDOMElement = (text: string) => {
  const parts = text.split(/(<em>.*?<\/em>)/);

  return parts.map((part, index) => {
    if (part.startsWith('<em>') && part.endsWith('</em>')) {
      const content = part.slice(4, -5);
      return <em key={index}>{content}</em>;
    }
    return part;
  });
};

// New: Highlight with yellow background
/**
 * Given the full content and an array of highlight HTML strings (with <em> tags),
 * returns a React array with all highlighted regions (from all highlight patterns)
 * merged and rendered as yellow highlights.
 */
/**
 * Extract all unique <em>...</em> values from an array of highlight HTML strings.
 */
export const extractHighlightWords = (highlights: string[]): string[] => {
  const words = new Set<string>();
  for (const h of highlights) {
    const matches = h.matchAll(/<em>(.*?)<\/em>/g);
    for (const match of matches) {
      if (match[1]) words.add(match[1]);
    }
  }
  return Array.from(words);
};

export const applyMultipleHighlightsToContent = (
  content: string,
  highlights: string[],
) => {
  if (!content || !highlights || highlights.length === 0) return content;

  // 1. Collect all highlight ranges from all highlight patterns
  type Range = { start: number; end: number };
  const ranges: Range[] = [];

  for (const highlightHtml of highlights) {
    // Parse highlightHtml into plain text and get the positions of <em>...</em>
    let idxInContent = 0;
    const regex = /<em>(.*?)<\/em>|([^<]+)/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(highlightHtml))) {
      if (match[1] !== undefined) {
        // This is a highlighted part
        const highlightText = match[1];
        // Find this highlightText in content, starting from idxInContent
        const foundIdx = content.indexOf(highlightText, idxInContent);
        if (foundIdx !== -1) {
          ranges.push({
            start: foundIdx,
            end: foundIdx + highlightText.length,
          });
          idxInContent = foundIdx + highlightText.length;
        }
      } else if (match[2] !== undefined) {
        // This is a non-highlighted part
        const plainText = match[2];
        // Advance idxInContent by the length of this plain text
        const foundIdx = content.indexOf(plainText, idxInContent);
        if (foundIdx !== -1) {
          idxInContent = foundIdx + plainText.length;
        }
      }
    }
  }

  // 2. Merge overlapping/adjacent ranges
  ranges.sort((a, b) => a.start - b.start);
  const mergedRanges: Range[] = [];
  for (const range of ranges) {
    if (
      mergedRanges.length > 0 &&
      range.start <= mergedRanges[mergedRanges.length - 1].end + 1
    ) {
      // Overlapping or adjacent, merge
      mergedRanges[mergedRanges.length - 1].end = Math.max(
        mergedRanges[mergedRanges.length - 1].end,
        range.end,
      );
    } else {
      mergedRanges.push({ ...range });
    }
  }

  // 3. Render content with highlights
  const result: React.ReactNode[] = [];
  let lastIdx = 0;
  mergedRanges.forEach((range, i) => {
    if (range.start > lastIdx) {
      result.push(content.slice(lastIdx, range.start));
    }
    result.push(
      <YellowHighlight key={i}>
        {content.slice(range.start, range.end)}
      </YellowHighlight>,
    );
    lastIdx = range.end;
  });
  if (lastIdx < content.length) {
    result.push(content.slice(lastIdx));
  }
  return result;
};

/**
 * Highlight all occurrences of the given words/phrases in the content.
 * Handles overlapping and multi-word phrases.
 */
export const highlightWordsInContent = (content: string, words: string[]) => {
  if (!content || !words || words.length === 0) return content;

  // If words are in sequence in content, highlight as a single phrase
  if (words.length > 1) {
    const phrase = words.join(' ');
    const phraseRegex = new RegExp(
      phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
      'i', // Only first occurrence, case-insensitive
    );
    const match = phraseRegex.exec(content);
    if (match) {
      const result: React.ReactNode[] = [];
      let lastIndex = 0;
      let key = 0;
      if (match.index > lastIndex) {
        result.push(content.slice(lastIndex, match.index));
      }
      result.push(
        <YellowHighlight key={key++}>
          {content.slice(match.index, match.index + match[0].length)}
        </YellowHighlight>,
      );
      lastIndex = match.index + match[0].length;
      if (lastIndex < content.length) {
        result.push(content.slice(lastIndex));
      }
      return result;
    }
  }

  // Fallback: highlight individual words/phrases
  const sortedWords = Array.from(new Set(words)).sort(
    (a, b) => b.length - a.length,
  );
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = sortedWords.map(escapeRegExp).join('|');
  const regex = new RegExp(pattern, 'gi');

  let lastIndex = 0;
  const result: React.ReactNode[] = [];
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(content))) {
    if (match.index > lastIndex) {
      result.push(content.slice(lastIndex, match.index));
    }
    result.push(
      <YellowHighlight key={key++}>
        {content.slice(match.index, regex.lastIndex)}
      </YellowHighlight>,
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < content.length) {
    result.push(content.slice(lastIndex));
  }
  return result;
};

export const Highlight = styled(TableHeader)`
  ${({ theme }) => theme.mixins.ellipsis};

  em {
    font-weight: bold;
  }
`;

export const YellowHighlight = styled('span')`
  background: #fff7b2;
  color: inherit;
  border-radius: 3px;
  padding: 0 2px;
  font-weight: 500;
`;
